import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { useAuth } from "@/context/AuthContext";
import { formatApiErrorDetail } from "@/lib/api";
import { Sparkle, Heart } from "@phosphor-icons/react";

const IMG = "https://images.unsplash.com/photo-1500305614571-ae5b6592e65d?crop=entropy&cs=srgb&fm=jpg&q=80&w=1200";

export default function Register() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ display_name: "", username: "", email: "", password: "", role: "fan" });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const upd = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setError("");
    try {
      await register(form);
      navigate(form.role === "creator" ? "/settings" : "/explore");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || "Registration failed");
    } finally { setBusy(false); }
  };

  const Role = ({ value, icon, label, desc }) => (
    <button type="button" data-testid={`role-${value}`} onClick={() => setForm({ ...form, role: value })}
      className={`flex-1 text-left p-4 border transition-colors ${form.role === value ? "border-primary bg-primary/10" : "border-border hover:border-foreground/40"}`}>
      <div className="mb-2 text-primary">{icon}</div>
      <p className="font-accent font-bold text-sm">{label}</p>
      <p className="text-xs text-muted-foreground">{desc}</p>
    </button>
  );

  return (
    <div className="min-h-[calc(100vh-4rem)] grid lg:grid-cols-2">
      <div className="flex items-center justify-center px-6 py-16 order-2 lg:order-1">
        <motion.form onSubmit={submit} data-testid="register-form"
          initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}
          className="w-full max-w-sm">
          <p className="font-accent text-xs uppercase tracking-[0.4em] text-primary mb-3">Join free</p>
          <h1 className="font-display text-4xl mb-8">Create account</h1>
          {error && <p data-testid="register-error" className="mb-4 text-sm text-primary bg-primary/10 border border-primary/30 px-3 py-2">{error}</p>}

          <div className="flex gap-3 mb-6">
            <Role value="fan" icon={<Heart size={22} weight="fill" />} label="I'm a Fan" desc="Browse & subscribe" />
            <Role value="creator" icon={<Sparkle size={22} weight="fill" />} label="I'm a Creator" desc="Post & earn" />
          </div>

          <div className="grid grid-cols-2 gap-3 mb-5">
            <div>
              <label className="block text-xs uppercase tracking-widest text-muted-foreground mb-2">Display name</label>
              <input data-testid="register-name" required value={form.display_name} onChange={upd("display_name")}
                className="w-full bg-card border border-border px-4 py-3 focus:border-primary outline-none transition-colors" />
            </div>
            <div>
              <label className="block text-xs uppercase tracking-widest text-muted-foreground mb-2">Username</label>
              <input data-testid="register-username" required value={form.username} onChange={upd("username")}
                className="w-full bg-card border border-border px-4 py-3 focus:border-primary outline-none transition-colors" />
            </div>
          </div>
          <label className="block text-xs uppercase tracking-widest text-muted-foreground mb-2">Email</label>
          <input data-testid="register-email" type="email" required value={form.email} onChange={upd("email")}
            className="w-full bg-card border border-border px-4 py-3 mb-5 focus:border-primary outline-none transition-colors" />
          <label className="block text-xs uppercase tracking-widest text-muted-foreground mb-2">Password</label>
          <input data-testid="register-password" type="password" required minLength={6} value={form.password} onChange={upd("password")}
            className="w-full bg-card border border-border px-4 py-3 mb-8 focus:border-primary outline-none transition-colors" />
          <button data-testid="register-submit" disabled={busy}
            className="w-full py-4 bg-primary text-primary-foreground font-accent font-bold uppercase tracking-widest text-sm transition-transform active:scale-95 hover:bg-[#e6004c] disabled:opacity-60">
            {busy ? "Creating…" : "Create account"}
          </button>
          <p className="mt-6 text-sm text-muted-foreground text-center">
            Already a member? <Link to="/login" data-testid="to-login" className="text-primary hover:underline">Log in</Link>
          </p>
        </motion.form>
      </div>
      <div className="hidden lg:block relative order-1 lg:order-2">
        <img src={IMG} alt="" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-background via-background/30 to-transparent" />
        <div className="absolute bottom-10 left-10 right-10">
          <h2 className="font-display text-5xl leading-none">Your fantasy, <span className="italic text-primary">your rules</span>.</h2>
        </div>
      </div>
    </div>
  );
}
